import React from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Hidden, Stack } from "@origin-digital/ods-core";
import { findLabel, SearchLevel } from "../helpers/mapping";
import { IAppContext } from "../AppContext";
import { ArticleBodyController } from "../components/ArticleBody";
import { LoginViewMessagesController } from "../components/LoginViewMessages";
import { routes } from "../helpers/router.helper";
import {
  IArticleAPIResponse,
  IMetaData,
  Paths,
} from "../../../shared/interface/article";
import { getHostName } from "../helpers/url.helper";
import { UseParamsType } from "../router";
import { findArticleLabel } from "../services/label";
import { useDaxi } from "../services/useDaxi";
import metaContent from "../cms/meta.json";
import { ScreenTemplate } from "./ScreenTemplate";

interface IProps {
  context: IAppContext;
}

export const Article = ({ context }: IProps) => {
  const {
    category,
    subCategory,
    articleName,
    articleId,
  } = useParams() as UseParamsType;
  const history = useHistory();
  const location = useLocation();

  const categories = context.navigation.data
    ? context.navigation.data.categories
    : [];
  const data = findLabel(categories, category, SearchLevel.SUBCATEGORY);
  const title = data ? data.displayName : "";
  const description = data ? data.metaDesc : "";

  const articleLabel = findArticleLabel(
    articleId,
    context.articleLabels,
    context.navigation
  );
  const articleMetaData: IMetaData = {
    canonicalUrl: `${getHostName()}${routes.article(
      articleLabel.category,
      articleLabel.subcategory,
      articleId,
      articleName
    )}`,
  };

  const articleRes = useDaxi<IArticleAPIResponse>(Paths.FullArticleData, {
    params: { articleId },
    ttlInMin: context.config.ttl,
    xhr: true,
  });

  //get full article data for seo meta data
  if (articleRes.data) {
    const { metaTitle, metaDescription, canonicalUrl } = articleRes.data;
    if (canonicalUrl) {
      const articleUrl = routes.article(
        category,
        subCategory,
        articleId,
        canonicalUrl
      );
      // prevents an infinite loop
      if (location.pathname !== articleUrl) {
        history.replace(articleUrl);
      }
      const curl = `${getHostName()}${routes.article(
        articleLabel.category,
        articleLabel.subcategory,
        articleId,
        canonicalUrl
      )}`;
      articleMetaData.canonicalUrl = curl;
    }
    if (metaTitle) {
      articleMetaData.metaTitle = `${metaContent.titlePrefix}${metaTitle}`;
    }
    articleMetaData.metaDescription = metaDescription;
  }

  return (
    <ScreenTemplate
      title={title}
      description={description}
      isNative={context.isNative}
      isArticle={true}
      metaData={articleMetaData}
      data-id="article"
    >
      <Stack space="xlarge">
        <Stack space="medium">
          <ArticleBodyController articleId={articleId} />
          {!context.isNative && (
            <Hidden above="md">
              <Stack space={["large", "xlarge"]}>
                <LoginViewMessagesController />
              </Stack>
            </Hidden>
          )}
        </Stack>
      </Stack>
    </ScreenTemplate>
  );
};
